import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DeviceStoreActions, DeviceStoreSelectors, SelectedLocation } from '@era/stores';
import { Action, Store } from '@ngrx/store';
import { CategoryType, isCategoryType } from '@rocksoft/shared';
import { combineLatest, merge, Observable, ReplaySubject } from 'rxjs';
import { filter, first, map, withLatestFrom } from 'rxjs/operators';
import { DeviceLinkSelectorComponent } from './device-link-selector/device-link-selector.component';

/** Category types that use the location/room selector and a single "mark as X" action. */
type SelectorCategoryType = 'Bar' | 'FrontDesk' | 'Housekeeping' | 'Kiosk' | 'Staff' | 'Location' | 'Brand' | 'None';

const categoryActions: Record<
  SelectorCategoryType,
  (payload: { deviceLink: unknown; brandId?: string; locationId?: string }) => Action
> = {
  Bar: DeviceStoreActions.DeviceBarViewActions.deviceMarkedAsBar,
  FrontDesk: DeviceStoreActions.DeviceFrontDeskViewActions.deviceMarkedAsFrontDesk,
  Housekeeping: DeviceStoreActions.DeviceHousekeepingViewActions.deviceMarkedAsHousekeeping,
  Kiosk: DeviceStoreActions.DeviceKioskViewActions.deviceMarkedAsKiosk,
  Staff: DeviceStoreActions.DeviceStaffViewActions.deviceMarkedAsStaff,
  Location: DeviceStoreActions.DeviceAssignViewActions.deviceMarkedAsSpare,
  Brand: DeviceStoreActions.DeviceUnassignViewActions.deviceUnassigned,
  None: DeviceStoreActions.DeviceUnassignViewActions.deviceUnavailable,
};

const displayLabels: Record<CategoryType | 'None', string> = {
  Bar: 'Bar',
  FrontDesk: 'Front desk',
  Housekeeping: 'Housekeeping',
  Kiosk: 'Kiosk',
  Staff: 'Staff',
  Location: 'Spare',
  Brand: 'Unassign',
  Room: 'Assign',
  None: 'Unavailable',
};

@Component({
  selector: 'era-device-link',
  templateUrl: './device-link.container.html',
  imports: [DeviceLinkSelectorComponent, RouterLink, AsyncPipe],
})
export class DeviceLinkContainerComponent {
  private readonly deviceStore = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public readonly category$: Observable<CategoryType | 'None'>;

  public readonly displayLabel$: Observable<string>;
  public readonly showSelector$: Observable<boolean>;
  public readonly submitButtonClass$: Observable<string>;
  public readonly submitButtonLabel$: Observable<string>;

  private readonly selectionSubject = new ReplaySubject<SelectedLocation>(1);
  public readonly data$: Observable<SelectedLocation | undefined>;
  public readonly selection$: Observable<SelectedLocation | undefined>;

  constructor() {
    this.category$ = this.route.data.pipe(
      map((data) => data['categoryType']),
      filter((cat) => isCategoryType(cat) || cat === 'None')
    );

    this.displayLabel$ = this.category$.pipe(map((cat) => displayLabels[cat]));

    this.showSelector$ = this.category$.pipe(map((cat) => cat !== 'None' && cat !== 'Brand'));

    this.submitButtonClass$ = this.category$.pipe(
      map((cat) => {
        if (cat === 'None') return 'btn btn-danger me-2';
        if (cat === 'Brand') return 'btn btn-warning me-2';
        return 'btn btn-success me-2';
      })
    );

    this.data$ = this.deviceStore.select(DeviceStoreSelectors.selectSelectedLocation);
    this.selection$ = merge(this.data$, this.selectionSubject);

    this.submitButtonLabel$ = combineLatest([this.category$, this.selection$]).pipe(
      map(([cat, selection]) => {
        if (cat === 'None') return 'Confirm';
        if (cat === 'Brand') return 'Unassign';
        if (cat === 'Location' && selection?.roomId) return displayLabels.Room;
        return displayLabels[cat];
      })
    );
  }

  /** True when selection is for room (assigned device); used for Spare warning. */
  public isAssignedDevice(): Observable<boolean> {
    return this.selection$.pipe(
      map((selection) => !!(selection?.brandId && selection?.locationId && selection?.roomId))
    );
  }

  public updateSelection(selectedLocation: SelectedLocation): void {
    this.selectionSubject.next(selectedLocation);
  }

  public markAsCategory(): void {
    const store = this.deviceStore;
    const link$ = store.select(DeviceStoreSelectors.selectCurrentDeviceLink);

    // For room/location flows, mark as spare or assign to room
    link$.pipe(withLatestFrom(this.category$, this.selection$), first()).subscribe(([deviceLink, cat, selection]) => {
      if (
        selection !== undefined &&
        deviceLink !== undefined &&
        selection.brandId !== undefined &&
        selection.locationId !== undefined
      ) {
        if (cat === 'Room' || cat === 'Location') {
          if (selection.roomId === undefined) {
            store.dispatch(
              DeviceStoreActions.DeviceAssignViewActions.deviceMarkedAsSpare({
                deviceLink,
                brandId: selection.brandId,
                locationId: selection.locationId,
              })
            );
          } else {
            store.dispatch(
              DeviceStoreActions.DeviceAssignViewActions.deviceAssigned({
                deviceLink,
                brandId: selection.brandId,
                locationId: selection.locationId,
                roomId: selection.roomId,
              })
            );
          }
        } else {
          // For other categories, mark as category
          const action = categoryActions[cat as SelectorCategoryType]({
            deviceLink,
            brandId: selection?.brandId,
            locationId: selection?.locationId,
          });
          store.dispatch(action);
        }
        void this.router.navigate(['../'], { relativeTo: this.route });
      }
    });
  }
}
